import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import { autorun } from 'mobx';
import { observer } from 'mobx-react';

import Localizable from 'lib/intl/localizable';
import Styled from 'styled';
import ScrollView from 'lib/scroll/scroll-view';
import withCompute from 'lib/mobx/with-compute';
import compute from 'lib/mobx/compute';
import computed from 'lib/mobx/computed';

import CommentView, { CommentViewModel } from 'four-nets/common/comment/comment-view';
import PagerView from 'four-nets/ui/pager-view';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import UserGridListStackView from 'four-nets/user/user-grid-list-stack-view';
import ComposeMailDialog from 'four-nets/mails/compose-mail/compose-mail-dialog';

import NewBlogCommentView, { NewBlogCommentViewModel } from '../new-blog-comment-view';
import defaultStyles from './blog-comments-view.scss';

export { BlogCommentsViewModel };

@withCompute()
class BlogCommentsViewModel {
    @compute comments: PagedResult<Comment>;
    @compute loading;

    constructor(options) {
        const { post, urlBuilder, mailStore, refresh, intl } = options;

        this.urlBuilder = urlBuilder;
        this.mailStore = mailStore;
        this.post = post;
        this.intl = intl;

        this.newBlogCommentViewModel = new NewBlogCommentViewModel({
            post,
            refresh,
            intl,
        });
    }

    @computed get commentViewModels(): CommentViewModel[] {
        return this.comments.results.map(
            comment =>
                new CommentViewModel({
                    comment,
                    urlBuilder: this.urlBuilder,
                    mailStore: this.mailStore,
                    userMentionable: this.newBlogCommentViewModel,
                })
        );
    }

    toJSON() {
        return null;
    }
}

@Localizable()
@Styled(defaultStyles)
@observer
class BlogCommentsView extends React.Component {
    static contextTypes = {
        scrollView: PropTypes.object,
    };

    static childContextTypes = {
        showLikers: PropTypes.func,
        writeMailToAuthor: PropTypes.func,
    };

    state = { open: false, userGridListViewModel: null, composeMailOpen: false, to: [], subject: '', body: '' };

    getChildContext() {
        return {
            showLikers: this.showCommentLikers,
            writeMailToAuthor: this.writeMailToCommentAuthor,
        };
    }

    ref: ?HTMLDivElement;

    setRef = ref => {
        this.ref = ref;
    };

    componentDidMount() {
        const disposers = [
            autorun(() => {
                const ref = this.context.scrollView.ref;
                if (ref && this.props.blogCommentsViewModel.loading) {
                    this.context.scrollView.scrollToRef({
                        ref: this.ref,
                        offset: -90,
                    });
                }
            }),
        ];

        this.disposer = () => {
            disposers.forEach(disposer => {
                disposer();
            });
        };
    }

    componentWillUnmount() {
        this.disposer();
    }

    showCommentLikers = userGridListViewModel => {
        this.setState({ userGridListViewModel, open: true });
    };

    showComposeMail = () => {
        this.setState({ composeMailOpen: true });
    };

    closeComposeMail = () => {
        this.setState({ composeMailOpen: false });
    };

    writeMailToCommentAuthor = comment => {
        const magazineTitle = this.props.intl.formatMessage(defineMessages({ MAGAZINE: { id: 'MAGAZINE' } }).MAGAZINE);
        const subject = `${magazineTitle} -> ${this.props.blogCommentsViewModel.post.title.rendered}`;

        this.setState({
            to: [comment.user.username],
            subject,
            composeMailOpen: true,
        });
    };

    render() {
        const { blogCommentsViewModel, styles } = this.props;
        const { commentViewModels, comments, newBlogCommentViewModel, loading } = blogCommentsViewModel;

        const classNames = [styles.blogComments];

        if (comments.pages > 1) {
            classNames.push(styles.paged);
        }

        return (
            <div ref={this.setRef}>
                <div className={classNames.join(' ')} />
                <h2 className={styles.heading}>
                    <FormattedMessage id='COMMENTS' />
                </h2>
                <LoadingWrapperView loading={loading}>
                    <ul className={styles.list}>
                        {commentViewModels.map(commentViewModel => (
                            <li key={commentViewModel.comment.id} className={styles.item}>
                                <CommentView commentViewModel={commentViewModel} />
                            </li>
                        ))}
                    </ul>
                    <div className={styles.pager}>
                        <PagerView count={comments.pages} current={comments.page} />
                    </div>
                    <div className={styles.newComment} />
                    <NewBlogCommentView newBlogCommentViewModel={newBlogCommentViewModel} />
                </LoadingWrapperView>
                <UserGridListStackView
                    open={Boolean(this.state.userGridListViewModel && this.state.open)}
                    onClose={() => {
                        this.setState({ open: false });
                    }}
                    title={<FormattedMessage id='LIKE' />}
                    userGridListViewModel={this.state.userGridListViewModel}
                />
                <ComposeMailDialog {...this.state} open={this.state.composeMailOpen} onClose={this.closeComposeMail} />
            </div>
        );
    }
}

export default BlogCommentsView;
